/**
 * Document Attributes Plugin - Type Definitions
 *
 * This module defines the types for the DocumentAttributesPlugin which stores
 * document-level attributes in plugin state instead of doc.attrs to prevent
 * NodeView recreation on updates.
 */

import type { PluginKey } from "@tiptap/pm/state";
import type { LabwareObject } from "@/automation/labware/labwareManager";
import type { RunConfiguration } from "@/automation/run/RunConfigurationExtension";

// ============================================================================
// PLUGIN STATE
// ============================================================================

/**
 * The shape of selectOptions (labware storage)
 */
export interface SelectOptionsState {
  labware: LabwareObject[];
}

/**
 * The complete plugin state containing all document-level attributes
 */
export interface DocumentAttributesState {
  selectOptions: SelectOptionsState;
  runConfiguration: RunConfiguration | null;
}

/**
 * Default values for initialization
 */
export const DEFAULT_DOCUMENT_ATTRIBUTES: DocumentAttributesState = {
  selectOptions: {
    labware: [],
  },
  runConfiguration: null,
};

// ============================================================================
// PLUGIN ACTIONS (setMeta payloads)
// ============================================================================

/**
 * Action types for modifying plugin state via transaction metadata
 */
export type DocumentAttributesAction =
  | SetSelectOptionsAction
  | SetLabwareAction
  | UpdateLabwareAction
  | DeleteLabwareAction
  | SetRunConfigurationAction
  | InitFromSourceAction;

interface SetSelectOptionsAction {
  type: "SET_SELECT_OPTIONS";
  payload: SelectOptionsState;
}

interface SetLabwareAction {
  type: "SET_LABWARE";
  payload: LabwareObject[];
}

interface UpdateLabwareAction {
  type: "UPDATE_LABWARE";
  payload: {
    id: string;
    updates: Partial<LabwareObject>;
  };
}

interface DeleteLabwareAction {
  type: "DELETE_LABWARE";
  payload: {
    id: string;
  };
}

interface SetRunConfigurationAction {
  type: "SET_RUN_CONFIGURATION";
  payload: RunConfiguration | null;
}

/**
 * Special action for initialization from doc.attrs or Y.Map
 */
interface InitFromSourceAction {
  type: "INIT_FROM_SOURCE";
  payload: Partial<DocumentAttributesState>;
}

// Re-export for convenience
export type { LabwareObject, RunConfiguration };
